import React, { useRef, useEffect, useState } from "react";
import { Group, Path, Text, Transformer, Rect, Circle } from "react-konva";

export default function SpeechBubble({ 
  x, 
  y, 
  text, 
  fontFamily = "Comic Sans MS", 
  fontSize = 16, 
  fontColor = "#000000",
  width = 160,
  height = 100,
  tailBaseX = 60,
  tailTipX = 40,
  tailTipY = 120,
  flipped = false,
  selected, 
  onSelect,
  onChange,
  drawingMode
}) {
  const groupRef = useRef();
  const bodyRef = useRef();
  const boundsRef = useRef();
  const trRef = useRef();

  const [tempBaseX, setTempBaseX] = useState(null);
  const [tempTipX, setTempTipX] = useState(null);
  const [tempTipY, setTempTipY] = useState(null);

  const CORNER_RADIUS = 20;
  const TAIL_BASE_HALF = 10; // half width of tail base

  useEffect(() => {
    if (selected && boundsRef.current && trRef.current) {
      trRef.current.nodes([boundsRef.current]);
      trRef.current.getLayer().batchDraw();
    }
  }, [selected]);

  const handleTransform = () => {
    if (boundsRef.current && onChange) {
      const node = boundsRef.current; // bounds rect (body + tail)
      const scaleX = node.scaleX();
      const scaleY = node.scaleY();

      const newBoundsWidth = Math.max(CORNER_RADIUS * 2 + 40, node.width() * scaleX);
      const newBoundsHeight = Math.max(CORNER_RADIUS * 2 + 40 + ((tempTipY ?? tailTipY) - height), node.height() * scaleY);

      // Body height is bounds height minus tail protrusion
      const newBodyWidth = newBoundsWidth;
      const newBodyHeight = Math.max(CORNER_RADIUS * 2 + 40, height * scaleY);

      const newX = x + node.x();
      const newY = y + node.y();

      // normalize
      node.scaleX(1);
      node.scaleY(1);
      node.position({ x: 0, y: 0 });
      node.width(newBoundsWidth);
      node.height(newBoundsHeight);

      // scale tail positions relative to old width/height
      const sx = newBodyWidth / width;
      const sy = newBodyHeight / height;

      const oldBaseX = tempBaseX ?? tailBaseX;
      const oldTipX = tempTipX ?? tailTipX;
      const oldTipY = tempTipY ?? tailTipY;

      const scaledTailBaseX = Math.min(Math.max(CORNER_RADIUS, oldBaseX * sx), newBodyWidth - CORNER_RADIUS);
      const scaledTailTipX = oldTipX * sx;
      const scaledTailTipY = newBodyHeight + Math.max(6, (oldTipY - height) * sy);

      setTempBaseX(null);
      setTempTipX(null);
      setTempTipY(null);

      onChange({ 
        x: newX, 
        y: newY, 
        width: newBodyWidth, 
        height: newBodyHeight,
        tailBaseX: scaledTailBaseX,
        tailTipX: scaledTailTipX,
        tailTipY: scaledTailTipY
      });
    }
  };

  const baseXEffective = tempBaseX ?? tailBaseX;
  const tipXEffective = tempTipX ?? tailTipX;
  const tipYEffective = tempTipY ?? tailTipY;

  // Compute mirrored coordinates for rendering when flipped
  const renderTailBaseX = flipped ? width - baseXEffective : baseXEffective;
  const renderTailTipX = flipped ? width - tipXEffective : tipXEffective;
  const baseLeftX = Math.max(CORNER_RADIUS, renderTailBaseX - TAIL_BASE_HALF);
  const baseRightX = Math.min(width - CORNER_RADIUS, renderTailBaseX + TAIL_BASE_HALF);
  const clampedTipX = renderTailTipX;
  const clampedTipY = Math.max(height + 6, tipYEffective); // ensure below body
  
  // Create seamless bubble path with integrated tail
  const seamlessBubblePath = `
    M ${CORNER_RADIUS} 0
    H ${width - CORNER_RADIUS}
    Q ${width} 0 ${width} ${CORNER_RADIUS}
    V ${height - CORNER_RADIUS}
    Q ${width} ${height} ${width - CORNER_RADIUS} ${height}
    H ${baseRightX}
    L ${clampedTipX} ${clampedTipY}
    L ${baseLeftX} ${height}
    H ${CORNER_RADIUS}
    Q 0 ${height} 0 ${height - CORNER_RADIUS}
    V ${CORNER_RADIUS}
    Q 0 0 ${CORNER_RADIUS} 0
    Z`;

  const clamp = (val, min, max) => Math.min(Math.max(val, min), max);

  return (
    <Group 
      ref={groupRef}
      x={x} 
      y={y}
      onClick={onSelect} 
      draggable={!drawingMode}
      onDragEnd={(e) => {
        if (onChange) {
          onChange({ x: e.target.x(), y: e.target.y() });
        }
      }}
    >
      {/* Invisible bounds rect that includes tail for accurate transformer alignment */}
      <Rect
        ref={boundsRef}
        x={0}
        y={0}
        width={width}
        height={Math.max(height, clampedTipY)}
        fillEnabled={false}
        strokeEnabled={false}
        onTransformEnd={handleTransform}
      />

      {/* Seamless bubble with integrated tail */}
      <Path
        ref={bodyRef}
        data={seamlessBubblePath}
        fill="white"
        stroke="black"
        strokeWidth={2}
      />

      {/* Text */}
      <Text 
        text={text || "Hello!"} 
        x={CORNER_RADIUS}
        y={CORNER_RADIUS}
        fontSize={fontSize} 
        fontFamily={fontFamily}
        fill={fontColor}
        width={width - CORNER_RADIUS * 2}
        height={height - CORNER_RADIUS * 2}
        align="center"
        verticalAlign="middle"
      />

      {selected && (
        <>
          {/* draggable anchors for tail base and tip */}
          <Circle
            x={clamp(renderTailBaseX, CORNER_RADIUS, width - CORNER_RADIUS)}
            y={height}
            radius={6}
            fill="#4a90e2"
            stroke="white"
            strokeWidth={2}
            draggable
            onMouseDown={(e) => { e.cancelBubble = true; groupRef.current && groupRef.current.draggable(false); }}
            onDragStart={(e) => { e.cancelBubble = true; groupRef.current && groupRef.current.draggable(false); }}
            onDragMove={(e) => {
              e.cancelBubble = true;
              const nx = clamp(e.target.x(), CORNER_RADIUS, width - CORNER_RADIUS);
              // hard lock Y to bottom edge during drag to prevent jump
              e.target.position({ x: nx, y: height });
              const storedX = flipped ? width - nx : nx;
              setTempBaseX(storedX);
            }}
            onDragEnd={(e) => {
              e.cancelBubble = true;
              const nx = clamp(e.target.x(), CORNER_RADIUS, width - CORNER_RADIUS);
              e.target.position({ x: nx, y: height });
              const storedX = flipped ? width - nx : nx;
              setTempBaseX(null);
              if (onChange) onChange({ tailBaseX: storedX });
              groupRef.current && groupRef.current.draggable(true);
            }}
          />
          <Circle
            x={clampedTipX}
            y={clampedTipY}
            radius={6}
            fill="#4a90e2"
            stroke="white"
            strokeWidth={2}
            draggable
            dragBoundFunc={(pos) => ({ x: pos.x, y: Math.max(height + 6, pos.y) })}
            onMouseDown={(e) => { e.cancelBubble = true; groupRef.current && groupRef.current.draggable(false); }}
            onDragStart={(e) => { e.cancelBubble = true; groupRef.current && groupRef.current.draggable(false); }}
            onDragMove={(e) => {
              e.cancelBubble = true;
              const nx = e.target.x();
              const ny = Math.max(height + 6, e.target.y());
              const storedX = flipped ? width - nx : nx;
              setTempTipX(storedX);
              setTempTipY(ny);
            }}
            onDragEnd={(e) => {
              e.cancelBubble = true;
              const nx = e.target.x();
              const ny = Math.max(height + 6, e.target.y());
              const storedX = flipped ? width - nx : nx;
              setTempTipX(null);
              setTempTipY(null);
              if (onChange) onChange({ tailTipX: storedX, tailTipY: ny });
              groupRef.current && groupRef.current.draggable(true);
            }}
          />
          <Transformer 
            ref={trRef}
            ignoreStroke={true}
            boundBoxFunc={(oldBox, newBox) => {
              // enforce minimum size
              if (newBox.width < CORNER_RADIUS * 2 + 40 || newBox.height < CORNER_RADIUS * 2 + 40) {
                return oldBox;
              }
              return newBox;
            }}
          />
        </>
      )}
    </Group>
  );
}
